import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  ArrowRight, 
  ArrowLeft,
  TrendingUp,
  Users,
  DollarSign,
  Zap
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';

const GOALS = [
  { id: 'increase_traffic', label: 'Increase Organic Traffic', icon: TrendingUp, color: 'bg-blue-100 text-blue-600' },
  { id: 'generate_leads', label: 'Generate More Leads', icon: Users, color: 'bg-emerald-100 text-emerald-600' },
  { id: 'increase_revenue', label: 'Increase Revenue', icon: DollarSign, color: 'bg-amber-100 text-amber-600' },
  { id: 'brand_awareness', label: 'Build Brand Awareness', icon: Zap, color: 'bg-purple-100 text-purple-600' },
];

const INDUSTRIES = [
  'Technology', 'E-commerce', 'Healthcare', 'Finance', 'Real Estate',
  'Education', 'Manufacturing', 'Professional Services', 'Retail', 'Other'
];

export default function GoalsStep({ data, onNext, onBack }) {
  const [primaryGoal, setPrimaryGoal] = useState(data?.primary_goal || '');
  const [trafficTarget, setTrafficTarget] = useState(data?.target_traffic_increase || '');
  const [leadsTarget, setLeadsTarget] = useState(data?.target_leads_monthly || '');
  const [revenueTarget, setRevenueTarget] = useState(data?.target_revenue_impact || '');
  const [industry, setIndustry] = useState(data?.industry || '');
  const [competitors, setCompetitors] = useState(data?.competitors?.join(', ') || '');
  const [notes, setNotes] = useState(data?.additional_notes || '');
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!primaryGoal) {
      toast.error('Please select your primary goal');
      return;
    }

    setLoading(true);
    try {
      await api.post('/onboarding/goals', {
        primary_goal: primaryGoal,
        target_traffic_increase: trafficTarget ? parseInt(trafficTarget) : null,
        target_leads_monthly: leadsTarget ? parseInt(leadsTarget) : null,
        target_revenue_impact: revenueTarget ? parseFloat(revenueTarget) : null,
        industry: industry,
        competitors: competitors ? competitors.split(',').map(c => c.trim()) : [],
        additional_notes: notes
      });
      toast.success('Goals saved');
      onNext();
    } catch (error) {
      toast.error('Failed to save goals');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold font-heading mb-2">Set Your Goals</h1>
        <p className="text-slate-600 max-w-lg mx-auto">
          Help us understand your business objectives so we can create a tailored strategy for your success.
        </p>
      </div>

      {/* Primary Goal Selection */}
      <Card className="gds-card">
        <CardHeader>
          <CardTitle>What's your primary goal?</CardTitle>
          <CardDescription>Select the main objective you want to achieve</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={primaryGoal} onValueChange={setPrimaryGoal} className="grid grid-cols-2 gap-4">
            {GOALS.map((goal) => (
              <div key={goal.id}>
                <RadioGroupItem value={goal.id} id={goal.id} className="peer sr-only" />
                <Label
                  htmlFor={goal.id}
                  className="flex items-center gap-3 rounded-xl border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-indigo-600 peer-data-[state=checked]:bg-indigo-50 cursor-pointer transition-all"
                >
                  <div className={`w-10 h-10 rounded-lg ${goal.color} flex items-center justify-center`}>
                    <goal.icon className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{goal.label}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Target Metrics */}
      <Card className="gds-card">
        <CardHeader>
          <CardTitle>Target Metrics (Optional)</CardTitle>
          <CardDescription>Set specific targets to measure success</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="traffic">Traffic Increase (%)</Label>
              <Input
                id="traffic"
                type="number"
                placeholder="e.g., 50"
                value={trafficTarget}
                onChange={(e) => setTrafficTarget(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="leads">Monthly Leads Target</Label>
              <Input
                id="leads"
                type="number"
                placeholder="e.g., 100"
                value={leadsTarget}
                onChange={(e) => setLeadsTarget(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="revenue">Revenue Impact ($)</Label>
              <Input
                id="revenue"
                type="number"
                placeholder="e.g., 50000"
                value={revenueTarget}
                onChange={(e) => setRevenueTarget(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Industry & Competitors */}
      <Card className="gds-card">
        <CardHeader>
          <CardTitle>Industry & Competition</CardTitle>
          <CardDescription>Help us understand your market</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="competitors">Main Competitors (comma-separated URLs)</Label>
              <Input
                id="competitors"
                placeholder="e.g., competitor1.com, competitor2.com"
                value={competitors}
                onChange={(e) => setCompetitors(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any other information about your business goals..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          onClick={handleContinue}
          disabled={!primaryGoal || loading}
          className="gds-btn-primary"
        >
          {loading ? 'Saving...' : 'Continue'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
